/**
 * Reference grid drawn on the analysis copy only (README 3.2).
 *
 * Thin magenta lines every GRID_STEP in normalized coordinates, labelled at
 * both ends, so the vision model can read coordinates against a visible scale.
 * The display copy and the local validation never see the grid.
 */

export const GRID_STEP = 0.1;

const LINE_COLOR = "rgba(255, 0, 255, 0.55)";
const LABEL_COLOR = "rgba(255, 0, 255, 0.95)";
const LABEL_STROKE = "rgba(255, 255, 255, 0.9)";

/** Interior grid positions: 0.1, 0.2 ... 0.9. */
const gridValues = () => {
  const count = Math.round(1 / GRID_STEP);
  return Array.from({ length: count - 1 }, (_, i) => (i + 1) / count);
};

const drawLabel = (context, text, x, y) => {
  context.strokeText(text, x, y);
  context.fillText(text, x, y);
};

/**
 * Draws the grid in place on the given canvas.
 * @param {HTMLCanvasElement} canvas Analysis canvas, already rendered.
 */
export const drawReferenceGrid = (canvas) => {
  const { width, height } = canvas;
  const context = canvas.getContext("2d");
  if (!context || !width || !height) return;

  const values = gridValues();
  const fontSize = Math.max(10, Math.round(0.018 * Math.max(width, height)));
  const margin = Math.max(2, Math.round(fontSize * 0.3));

  context.save();

  // Lines at half-pixel offsets stay exactly 1 px wide.
  context.strokeStyle = LINE_COLOR;
  context.lineWidth = 1;
  context.beginPath();
  values.forEach((value) => {
    const x = Math.round(value * width) + 0.5;
    const y = Math.round(value * height) + 0.5;
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.moveTo(0, y);
    context.lineTo(width, y);
  });
  context.stroke();

  context.font = `bold ${fontSize}px sans-serif`;
  context.fillStyle = LABEL_COLOR;
  context.strokeStyle = LABEL_STROKE;
  context.lineWidth = Math.max(2, Math.round(fontSize / 5));
  context.lineJoin = "round";

  values.forEach((value) => {
    const text = value.toFixed(1);
    const x = Math.round(value * width);
    const y = Math.round(value * height);

    // x labels along the top and bottom edges, just right of the line.
    context.textAlign = "left";
    context.textBaseline = "top";
    drawLabel(context, text, x + margin, margin);
    context.textBaseline = "bottom";
    drawLabel(context, text, x + margin, height - margin);

    // y labels along the left and right edges, just below the line.
    context.textBaseline = "top";
    context.textAlign = "left";
    drawLabel(context, text, margin, y + margin);
    context.textAlign = "right";
    drawLabel(context, text, width - margin, y + margin);
  });

  context.restore();
};
